import { useMemo } from "react";

import { ToastContainer } from "react-toastify";
import { FaListCheck } from "react-icons/fa6";

import {
  DataTable,
  DateColumnFilter,
  SelectColumnFilter,
} from "../../DataTable";
import { formatDate } from "../../../helpers/date.util";
import OverlayTooltip from "../../OverlayTooltip";

import {
  useGetCustomerReturnRequestPermissions,
  useGetGlobalReturnPermissions,
  accessorReturnPermissionSettingStatus,
} from "./returnPermission.util";
import {
  EditPermissionActionCell,
  EditSelectedPermission,
  CreatePermission,
  GlobalSettings,
} from "./private";

/**
 * @typedef {Object} Customer
 * @property {string} customerId
 * @property {string} customerName
 * @property {string} customerAddress
 * @property {string} customerCity
 * @property {string} customerState
 * @property {string} customerZip
 * @property {string} reasonEntered
 * @property {string} enteredBy
 * @property {string} enteredDate
 * @property {string} permission
 */

function CustomerCellColumn({ row }) {
  const { original } = row;

  return (
    <div className="tw-flex tw-items-start tw-flex-col">
      <div className="tw-whitespace-normal">{original?.customerName}</div>
      <div className="tw-whitespace-normal">
        {original?.customerAddress} | {original?.customerCity},{" "}
        {original?.customerState} {original?.customerZip}
      </div>
    </div>
  );
}

function renderTopToolbar(instance) {
  const { toggleAllRowsSelected, isAllRowsSelected } = instance;

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-flex-grow tw-gap-x-1.5">
      <div>
        <OverlayTooltip
          label={isAllRowsSelected ? "Unselect All" : "Select All"}
        >
          <button
            type="button"
            className="btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2"
            onClick={() => {
              toggleAllRowsSelected();
            }}
          >
            <FaListCheck className="tw-text-2xl" />
          </button>
        </OverlayTooltip>
      </div>

      <CreatePermission />

      <EditSelectedPermission instance={instance} />

      <GlobalSettings />
    </div>
  );
}

/**
 * @param {{isDisabled: boolean, isByApproval:boolean, reason} | null} responseGlobalPermission
 */

const tableMessage = (responseGlobalPermission) => {
  if (responseGlobalPermission?.isDisabled) {
    return "Customer returns have been set to disabled globally.\n Please click the [gear icon] to edit the global return settings.";
  }

  if (responseGlobalPermission?.isByApproval) {
    return "Customer returns have been set to by approval globally.\n Please click the [gear icon] to edit the global return settings.";
  }

  return "No data found";
};

function ReturnPermissionSetting() {
  const {
    data: response,
    isLoading,
    isError,
  } = useGetCustomerReturnRequestPermissions();

  const {
    data: responseGlobalPermission,
    isLoading: isLoadingGlobalPermission,
    isError: isErrorGlobalPermission,
  } = useGetGlobalReturnPermissions();

  const data = useMemo(() => {
    if (
      responseGlobalPermission?.data?.isDisabled ||
      responseGlobalPermission?.data?.isByApproval
    ) {
      return [];
    }

    return Array.isArray(response?.data) ? response?.data : [];
  }, [responseGlobalPermission?.data, response?.data]);

  const columns = useMemo(
    () => [
      {
        Header: "Customer ID",
        accessor: "customerId",
      },
      {
        id: "customer",
        Header: "Customer",
        accessor: (originalRow) =>
          `${originalRow?.customerName} ${originalRow?.customerAddress} | ${originalRow?.customerCity}, ${originalRow?.customerState} ${originalRow?.customerZip}`,
        Cell: CustomerCellColumn,
        minWidth: 200,
        width: 200,
        maxWidth: 250,
      },
      {
        Header: "Reason Entered",
        accessor: "reasonEntered",
      },
      {
        Header: "Entered By",
        accessor: "enteredBy",
      },
      {
        Header: "Create Permission Date",
        accessor: "enteredDate",
        Cell: ({ value }) => formatDate(value, "MM/DD/YYYY hh:mm A"),
        Filter: DateColumnFilter,
        filter: "dateEqual",
      },
      {
        id: "returnPermissionStatus",
        Header: "Return Permission Status",
        accessor: accessorReturnPermissionSettingStatus,
        Filter: SelectColumnFilter,
        filter: "deepEqual",
      },
      {
        Header: () => <div className="tw-text-center tw-bg-white">Actions</div>,
        accessor: "id",
        Cell: EditPermissionActionCell,
        disableFilters: true,
        disableResizing: true,
        minWidth: 85,
        width: 85,
      },
    ],
    []
  );

  return (
    <div className="tw-rounded-2xl tw-bg-white tw-pb-5 tw-px-3">
      <DataTable
        className="tw-max-h-136"
        columns={columns}
        data={data}
        isLoading={isLoading || isLoadingGlobalPermission}
        isError={isError || isErrorGlobalPermission}
        noDataMessage={tableMessage(responseGlobalPermission?.data)}
        initialState={{
          sortBy: [
            {
              id: "customerId",
            },
          ],
        }}
        enableRowSelection
        renderWithTopToolbar={renderTopToolbar}
      />

      <ToastContainer
        containerId="return-permission-settings-toast"
        limit={2}
      />
    </div>
  );
}

export default ReturnPermissionSetting;
